// src/components/ClientTable.tsx
import { Table, Tag, Button, TableColumnsType, Tooltip } from "antd";
import { RightOutlined, CheckCircleOutlined } from "@ant-design/icons";
import { SortConfig } from "./type";
import { TClient } from "~/types";
import { calculateCubicVolume, capitalize, cn } from "~/utils";
import { NumericFormat } from "react-number-format";
import { ClientStatus, InventoryStatusCode, RateType } from "~/utils/constants";
import { useExportInventory } from "~/services/inventory";
import { useNotification } from "~/hooks";
import { Download } from "lucide-react";
import { useMemo } from "react";

interface ClientTableProps {
  sortedClients: TClient[];
  sortConfig: SortConfig;
  requestSort: (key: keyof TClient) => void;
  setSelectedClient: (client: TClient | null) => void;
  selectedClient: TClient | null;
  clientsData?: {
    items: TClient[];
    meta: {
      totalItems: number;
      itemCount: number;
      itemsPerPage: number;
      totalPages: number;
      currentPage: number;
    };
  };
  onPageChange?: (page: number, pageSize: number) => void;
  isLoading?: boolean;
}

export const ClientTable: React.FC<ClientTableProps> = ({
  sortedClients,
  sortConfig,
  requestSort,
  setSelectedClient,
  selectedClient,
  clientsData,
  onPageChange,
  isLoading,
}) => {
  const columns: TableColumnsType<TClient> = [
    {
      title: "ID",
      dataIndex: "clientRef",
      sorter: false, // Disable built-in sorting since we're using server-side
      sortOrder:
        sortConfig.key === "clientRef" ? sortConfig.direction : undefined,
      onHeaderCell: () => ({
        onClick: () => requestSort("clientRef"),
      }),
    },
    {
      title: "Client",
      key: "client",
      sorter: false, // Disable built-in sorting since we're using server-side
      sortOrder:
        sortConfig.key === "lastName" || sortConfig.key === "companyName"
          ? sortConfig.direction
          : undefined,
      onHeaderCell: () => ({
        onClick: () => requestSort("companyName"), // Use companyName as the primary sort
      }),
      render: (_, record) => {
        const fullName =
          record.companyName ||
          [(record.firstName, record.lastName)]
            .filter(Boolean)
            .join(" ")
            .trim() ||
          "-";
        const isStorageClient = record.rateType === RateType.STORAGE_CLIENT;
        return (
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span>{fullName}</span>
            {isStorageClient && (
              <Tooltip title="Storage Client">
                <CheckCircleOutlined style={{ color: "#52c41a", fontSize: 14 }} />
              </Tooltip>
            )}
          </div>
        );
      },
    },
    {
      title: "Items",
      dataIndex: "totalItems",
      render: (_, record) => {
        const items = record.inventoryClient?.length || 0;
        return items > 0 ? `${items} items` : "No items";
      },
    },
    {
      title: "Vol",
      dataIndex: "cubicVolume",
      render: (_, record) => {
        const volume = record.inventoryClient
          ?.filter((inv) => !inv.parentInventoryId)
          .reduce((acc, item) => {
            const volume = calculateCubicVolume(item) / 1728;
            return acc + volume;
          }, 0);
        return (
          <NumericFormat
            type="text"
            displayType="text"
            decimalScale={5}
            value={`${volume} ft³`}
          />
        );
      },
    },
    {
      title: "Monthly $",
      dataIndex: "monthlyCost",
      width: "12%",
      render: (_, record) => {
        // Group inventory by location to apply minimum per location
        const byLocation = record.inventoryClient
          ?.filter((inv) => !inv.parentInventoryId) // Only count parent items, not pieces inside
          .reduce((acc, item) => {
            const locationId = item.location?.id || 'no-location';
            if (!acc[locationId]) {
              acc[locationId] = {
                items: [],
                location: item.location,
              };
            }
            acc[locationId].items.push(item);
            return acc;
          }, {} as Record<string, { items: any[], location?: any }>) || {};

        // Calculate total cost applying minimum per location
        const totalMonthlyCost = Object.values(byLocation).reduce((total, { items, location }) => {
          // Sum cubic feet for this location
          const totalCubicFeet = items.reduce((sum, item) => {
            const volume = calculateCubicVolume(item) / 1728;
            return sum + volume;
          }, 0);

          // Apply minimum cubic feet for this location
          const minimumCubicFeet = Number(location?.minimumCubicFeet) || 0;
          const billableCubicFeet = Math.max(totalCubicFeet, minimumCubicFeet);

          // Calculate cost for this location
          const ratePerCubicFt = Number(location?.ratePerCubicFt) || 0;
          const locationCost = billableCubicFeet * ratePerCubicFt;

          // Debug log for first client only
          if (total === 0 && items.length > 0) {
            console.log(`Location: ${location?.name || 'No location'}`);
            console.log(`  Items: ${items.length}`);
            console.log(`  Total Cubic Feet: ${totalCubicFeet.toFixed(2)}`);
            console.log(`  Minimum Cubic Feet: ${minimumCubicFeet}`);
            console.log(`  Billable Cubic Feet: ${billableCubicFeet.toFixed(2)}`);
            console.log(`  Rate per Cu Ft: $${ratePerCubicFt}`);
            console.log(`  Location Cost: $${locationCost.toFixed(2)}`);
          }

          return total + locationCost;
        }, 0);

        return (
          <NumericFormat
            type="text"
            displayType="text"
            prefix="$"
            thousandSeparator=","
            decimalScale={2}
            fixedDecimalScale
            value={totalMonthlyCost}
          />
        );
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      width: "10%",
      render: (status: ClientStatus) => (
        <Tag color={status === ClientStatus.ACTIVE ? "green" : "default"}>
          {capitalize(status)}
        </Tag>
      ),
    },
    {
      title: "Export",
      width: "8%",
      align: "center",
      render: (_: unknown, record) => <DownloadCsv record={record} />,
    },
    {
      width: "5%",
      align: "center",
      title: "",
      render: (_: unknown, record) => (
        <Button
          type="link"
          icon={<RightOutlined />}
          onClick={() =>
            setSelectedClient(selectedClient?.id === record.id ? null : record)
          }
        />
      ),
    },
  ];

  return (
    <Table
      loading={isLoading}
      size="small"
      className="border rounded-lg"
      rootClassName="rounded-lg"
      columns={columns}
      dataSource={sortedClients}
      rowKey="id"
      pagination={{
        current: clientsData?.meta.currentPage || 1,
        total: clientsData?.meta.totalItems || 0,
        pageSize: clientsData?.meta.itemsPerPage || 20,
        showSizeChanger: true,
        showQuickJumper: true,
        showTotal: (total, range) =>
          `${range[0]}-${range[1]} of ${total} clients`,
        pageSizeOptions: ["10", "20", "50", "100"],
        onChange: (page, pageSize) => {
          onPageChange?.(page, pageSize);
        },
        onShowSizeChange: (_current, size) => {
          onPageChange?.(1, size); // Reset to page 1 when changing page size
        },
      }}
      rowClassName={(record) =>
        cn(
          "cursor-pointer",
          selectedClient?.id === record.id ? "ant-table-row-selected" : ""
        )
      }
      onRow={(record) => ({
        onClick: () =>
          setSelectedClient(selectedClient?.id === record.id ? null : record),
      })}
    />
  );
};

const DownloadCsv = ({ record }: { record: TClient }) => {
  const { mutate: exportInventoryByClient, isPending: isExporting } =
    useExportInventory();
  const { success, error } = useNotification();

  const handleExportClientInventory = (client: TClient) => {
    const clientName =
      client.companyName || `${client.firstName} ${client.lastName}`.trim();
    exportInventoryByClient(
      { clientId: client.id, status: InventoryStatusCode.AVAILABLE_ON_HAND },
      {
        onSuccess: () => {
          success({
            message: "Export successful",
            description: `Inventory data for ${clientName} has been exported to CSV`,
          });
        },
        onError: (err) => {
          error({
            message: "Export failed",
            description:
              err.message || "There was an error exporting the inventory data",
          });
        },
      }
    );
  };

  const hasInventory = useMemo(
    () => record.inventoryClient && record.inventoryClient.length > 0,
    [record.inventoryClient]
  );

  return (
    <Button
      type="text"
      size="small"
      icon={<Download size={16} />}
      loading={isExporting}
      disabled={!hasInventory}
      onClick={(e) => {
        e.stopPropagation();
        handleExportClientInventory(record);
      }}
      title={
        hasInventory ? "Export client inventory" : "No inventory to export"
      }
    />
  );
};
